//get all suppliers

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

import { toast } from 'react-toastify';
import {
  createGift,
  updateGift,
  deleteGift,
  GiftFormData,
  changeStatusGift,
} from '../api/gifts';
import { getGifts, GiftStatus, IGift } from '@/api/gifts';
import { SupplierDTO, parseSupplierDTO } from './useSuppliers';

// DTO cho customer
export interface GiftDTO {
  id: number;
  name: string;
  description: string;
  remainingQuantity: number;
  unitPrice: number;
  supplier: SupplierDTO;
  createdAt: string;
  updatedAt: string;
  deletedAt: string;
  status: GiftStatus;
}

// Chuyển đổi từ IOrderResponse sang OrderDTO
export const parseGiftDTO = (gift: IGift): GiftDTO => ({
  id: gift.id,
  name: gift.name,
  description: gift.description,
  remainingQuantity: gift.remainingQuantity,
  unitPrice: gift.unitPrice,
  supplier: parseSupplierDTO(gift.supplier),
  createdAt: gift.createdAt,
  updatedAt: gift.updatedAt,
  deletedAt: gift.deletedAt,
  status: gift.status,
});

// Lấy danh sách suppliers (tìm kiếm)
export const useGifts = ({ id, name }: { id: string; name: string }) => {
  return useQuery<{
    gifts: GiftDTO[];
  }>({
    queryKey: ['gifts', id, name],
    queryFn: async () => {
      const response = await getGifts(id, name);
      const gifts = response.map(parseGiftDTO);
      return {
        gifts,
      };
    },
    placeholderData: (previousData) => previousData,
  });
};
// Tạo gift
export const useCreateGift = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: GiftFormData) => {
      const response = await createGift(data);
      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['gifts'] });
      toast.success('Tạo quà tặng thành công');
    },
    onError: (error) => {
      toast.error(error.message);
    },
  });
};

// Cập nhật supplier
export const useUpdateGift = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: GiftFormData }) => {
      const response = await updateGift(id, data);
      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['gifts'] });
      toast.success('Cập nhật quà tặng thành công');
    },
    onError: (error) => {
      toast.error(error.message);
    },
  });
};

// Xóa gift
export const useDeleteGift = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const response = await deleteGift(id);
      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['gifts'] });
      toast.success('Xóa quà tặng thành công');
    },
    onError: (error) => {
      toast.error(error.message);
    },
  });
};

// Cập nhật trạng thái gift
export const useUpdateGiftStatus = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, status }: { id: number; status: GiftStatus }) => {
      queryClient
        .getQueryCache()
        .findAll({ queryKey: ['gifts'] })
        .forEach((query) => {
          const data = query.state.data as {
            gifts: GiftDTO[];
          };
          if (data && Array.isArray(data.gifts)) {
            queryClient.setQueryData(query.queryKey, {
              ...data,
              gifts: data.gifts.map((gift: GiftDTO) =>
                gift.id === id
                  ? {
                      ...gift,
                      status: status,
                    }
                  : gift
              ),
            });
          }
        });
      toast.success('Cập nhật trạng thái quà tặng thành công');
      const response = await changeStatusGift(id, status);
      return response;
    },
    onError: (error, { id, status }) => {
      queryClient
        .getQueryCache()
        .findAll({ queryKey: ['gifts'] })
        .forEach((query) => {
          const data = query.state.data as {
            gifts: GiftDTO[];
          };
          if (data && Array.isArray(data.gifts)) {
            queryClient.setQueryData(query.queryKey, {
              ...data,
              gifts: data.gifts.map((gift: GiftDTO) =>
                gift.id === Number(id)
                  ? {
                      ...gift,
                      status: status,
                    }
                  : gift
              ),
            });
          }
        });
      toast.error(error.message);
    },
  });
};
